import clsx from 'clsx'
import React, { FC } from 'react'
import type { CheckboxProps } from './types'
import { Typography } from '../Typography'

const STATE_TO_SVG_PATH_MAP = {
  indeterminate: <path d="M1 6h10" strokeWidth="1.6" />,
  selected: <path d="M0.5 5.5L5 9L11 2" strokeWidth="1.6" />,
  unselected: null,
} satisfies Record<CheckboxProps['state'], React.ReactNode>

export const Checkbox: FC<CheckboxProps> = ({
  testId = 'checkbox',
  id,
  label,
  name,
  value,
  state = 'unselected',
  disabled = false,
  className,
  labelClassName,
  onChange,
  onClick,
  labelTypographyProps,
  wrapperClassName,
}) => (
  <label
    data-testid={`${testId}-wrapper`}
    className={clsx(
      'flex items-center gap-x-2',
      { 'cursor-pointer': !disabled },
      wrapperClassName,
    )}
    role="checkbox"
    aria-checked={state === 'indeterminate' ? 'mixed' : state === 'selected'}
    aria-disabled={disabled}
    onClick={onClick}
  >
    <input
      type="checkbox"
      data-testid="default-checkbox"
      data-label={label}
      id={id}
      name={name}
      aria-describedby={name}
      checked={state === 'selected'}
      onChange={onChange}
      disabled={disabled}
      value={value}
      className="peer sr-only"
    />

    <div
      data-testid={testId}
      className={clsx(
        'p-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border border-content-action-default peer-focus-visible:ring-4 peer-focus-visible:peer-focus-visible:ring-offset-4 peer-focus-visible:ring-content-action-subtle',
        {
          'border-none bg-disabled': disabled,
          'bg-content-action-default': state !== 'unselected' && !disabled,
          'bg-white border-content-action-default':
            state === 'unselected' && !disabled,
        },
        className,
      )}
    >
      <svg
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={clsx({
          'stroke-icon-default-on-dark': !disabled,
          'stroke-icon-disabled': disabled,
        })}
      >
        {STATE_TO_SVG_PATH_MAP[state]}
      </svg>
    </div>

    {label && (
      <Typography
        testId={`${testId}-label`}
        className={labelClassName}
        size="sm"
        {...labelTypographyProps}
      >
        {label}
      </Typography>
    )}
  </label>
)
